import { Component, inject } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { Column } from 'ag-grid-enterprise';

import { SearchFieldComponent, ToggleComponent } from '@camelot/form-input';
import { InputCheckBox, InputTextBox } from '@camelot/form-model';
import { TranslatePipe } from '@camelot/translation';
import { ButtonComponent, EmptyComponent, TextComponent } from '@camelot/ui';

import { FilterModelSource } from '../../models/types';
import { CamTranslationGrid } from '../../translation.service';
import { AbstractGridComponent } from '../abstract-grid.component';

@Component({
  imports: [ButtonComponent, EmptyComponent, SearchFieldComponent, TextComponent, ToggleComponent, TranslatePipe],
  selector: 'cam-fields-selection',
  styleUrl: './fields-selection.component.scss',
  templateUrl: './fields-selection.component.html',
})
export class FieldsSelectionComponent extends AbstractGridComponent<any> {
  public readonly searchInput = new InputTextBox();

  private readonly _models = new Map<string, InputCheckBox>();
  private readonly _translate = inject(TranslateService);

  constructor() {
    super();
    CamTranslationGrid.getInstance();
  }

  get grid() {
    return this._grid;
  }

  public columns(): Column[] {
    const term = (this.searchInput.value ?? '').toLowerCase();
    return (this.grid.getColumns() ?? [])
      .filter(col => !term || this.displayLabel(col).toLowerCase().includes(term))
      .sort((a, b) => this.displayLabel(a).localeCompare(this.displayLabel(b)));
  }

  public displayLabel(col: Column): string {
    return this._translate.instant(this._label(col));
  }

  public flip(col: Column): void {
    const model = this._models.get(col.getId());
    if (!model || model.readonly) {
      return;
    }
    model.value = !model.value;
  }

  public hasColumns(): boolean {
    return this.columns().length > 0;
  }

  public model(col: Column): InputCheckBox {
    const id = col.getId();
    let model = this._models.get(id);
    if (!model) {
      model = new InputCheckBox({ readonly: this._isLastVisible(col), toggle: true, value: col.isVisible() });
      model.createFormControl();
      this._models.set(id, model);
    }
    return model;
  }

  public onToggle(col: Column, value: boolean): void {
    if (col.isVisible() === value) {
      return;
    }
    this.grid.api?.setColumnsVisible([col], value);
    this._persist();
    this._refreshLocks();
  }

  public reset(): void {
    this.grid.api?.resetColumnState();
    this._persist();
    this._syncModels();
  }

  public showAll(): void {
    this.grid.api?.setColumnsVisible(this.grid.getColumns() ?? [], true);
    this._persist();
    this._syncModels();
  }

  private _isLastVisible(col: Column): boolean {
    return col.isVisible() && this._visibleCount() <= 1;
  }

  private _label(col: Column): string {
    return col.getColDef().headerName ?? '';
  }

  private _persist(): void {
    const extract = this.grid.filters.extractCurrentFilterModel();
    this.grid.filters.applyFilterModel({ selectedFields: extract.selectedFields }, FilterModelSource.storage);
  }

  private _refreshLocks(): void {
    (this.grid.getColumns() ?? []).forEach(col => {
      const model = this._models.get(col.getId());
      if (!model) {
        return;
      }
      const lock = this._isLastVisible(col);
      if (model.readonly !== lock) {
        model.setReadonly(lock);
      }
    });
  }

  private _syncModels(): void {
    (this.grid.getColumns() ?? []).forEach(col => {
      const model = this._models.get(col.getId());
      if (model && model.value !== col.isVisible()) {
        model.value = col.isVisible();
      }
    });
    this._refreshLocks();
  }

  private _visibleCount(): number {
    return (this.grid.getColumns() ?? []).filter(col => col.isVisible()).length;
  }
}
